/**
 * Cloud Gateway API Service
 *
 * Service for managing Cloud Gateways (Alpine Linux network gateways)
 * for End Customers.
 */

import { apiClient } from './api'
import type { ConsoleTicket } from './consoleApi'

const BASE_URL = '/api/v1/end-customer'

// ============================================
// Types
// ============================================

export type CloudGatewayStatus =
  | 'pending'
  | 'provisioning'
  | 'configuring'
  | 'running'
  | 'stopped'
  | 'error'
  | 'deleting'
  | 'rebuilding'

export type ConnectionMode = 'direct' | 'relay'

/**
 * Origem do endereço da WAN.
 * - `dhcp`: lease da rede WAN do cluster (indisponível onde o provedor não roda DHCP)
 * - `pool`: IP público reservado no IPAM, incluso no produto de gateway
 * - `static`: endereçamento digitado manualmente
 */
export type GatewayWanMode = 'dhcp' | 'pool' | 'static'

export type WANConfig = {
  interface: string
  dhcp: boolean
  ip?: string | null
  gateway?: string | null
  netmask?: string | null
  dns: string[]
  mode: GatewayWanMode
  /** Id do PublicIPAddress reservado (só no modo 'pool'). */
  public_ip_id?: string | null
}

/** Item do GET /gateway/wan/available-ips. */
export type GatewayWanAvailableIP = {
  id: string
  ip_address: string
  gateway?: string | null
  netmask?: string | null
  dns_servers: string[]
  /** true = a organização já comprou este IPv4; usá-lo não consome outro do pool. */
  owned: boolean
}

export type GatewayWanIPsResponse = {
  ips: GatewayWanAvailableIP[]
  total: number
  /** false = a WAN do cluster nao entrega DHCP; o portal nao deve oferecer a opcao. */
  dhcp_available: boolean
}

/** Payload do PATCH /end-customer/gateway/{id}/wan. */
export type ChangeGatewayWanRequest = {
  wan_mode: GatewayWanMode
  wan_public_ip_id?: string | null
  wan_ip?: string | null
  wan_gateway?: string | null
  wan_netmask?: string | null
  wan_dns?: string[] | null
}

export type ChangeGatewayWanResponse = {
  gateway: CloudGateway
  wan_mode: GatewayWanMode
  wan_ip?: string | null
  /** true = a VM foi reiniciada para o cloud-init reaplicar a rede. */
  restarted: boolean
  message: string
}

export type LANConfig = {
  interface: string
  vnet_id?: string | null
  ip?: string | null
  netmask?: string | null
  cidr?: string | null
}

export type GatewayFeatures = {
  nat: boolean
  firewall: boolean
  vpn: boolean
  loadbalancer: boolean
  dhcp_server: boolean
}

export type GatewaySpecs = {
  cpu_cores: number
  memory_mb: number
  storage_gb: number
}

/**
 * VPC (NetworkPool) atualmente atachada ao gateway.
 * Source of truth: tabela vpc_gateway_attachments. Hoje sempre 1:1.
 */
export type AttachedVPC = {
  network_pool_id: string
  name: string
  network_identifier: string
  cidr?: string | null
}

export type CloudGateway = {
  id: string
  name: string
  organization_id: string
  cluster_id: string
  /** VPC atachada ao gateway (null pra GWs criados antes da migration multi-VPC). */
  attached_vpc?: AttachedVPC | null
  has_vm: boolean
  template_id?: string | null
  status: CloudGatewayStatus
  status_message?: string | null
  wan: WANConfig
  lan: LANConfig
  features: GatewayFeatures
  specs: GatewaySpecs
  // SSH access credentials
  initial_user?: string | null
  initial_password?: string | null
  // Connection mode fields
  connection_mode: ConnectionMode
  is_connected: boolean
  relay_id?: string | null
  mgmt_ip?: string | null
  last_heartbeat_at?: string | null
  // Timestamps
  created_at: string
  updated_at?: string | null
  provisioned_at?: string | null
  created_by?: string | null
}

/** Response do GET /end-customer/gateways (multi-GW). */
export type CloudGatewayListResponse = {
  gateways: CloudGateway[]
  total: number
}

/** Response do GET /end-customer/gateway/billing-preview?product_id=... */
export type GatewayBillingPreview = {
  product_id: string
  product_name: string
  /** Quantos GWs ativos a org já tem agora. */
  current_count: number
  /** Preço efetivo (0 quando is_billed_free). */
  unit_price: number
  /** base_price do Product, sem desconto. */
  original_unit_price: number
  currency: string
  billing_cycle: string
  is_billed_free: boolean
  /** Nome da política aplicada (ex.: "first_vpc_free") ou null. */
  policy?: string | null
}

/** Payload do PATCH /end-customer/gateway/{id}/network. */
export type ChangeGatewayNetworkRequest = {
  network_pool_id: string
}

export type CloudGatewayCreate = {
  name?: string
  product_id: string // Obrigatório - produto gateway B2B2B
  // WAN. `wan_mode` é a fonte de verdade; `wan_dhcp` segue aceito por compat e
  // é derivado no backend quando `wan_mode` vem omitido.
  wan_mode?: GatewayWanMode
  wan_public_ip_id?: string | null
  wan_dhcp?: boolean
  wan_ip?: string
  wan_gateway?: string
  wan_netmask?: string
  wan_dns?: string[]
  // LAN
  lan_vnet_id?: string
  lan_ip?: string
  lan_netmask?: string
  lan_cidr?: string
  // Features
  nat_enabled?: boolean
  firewall_enabled?: boolean
  vpn_enabled?: boolean
  dhcp_server_enabled?: boolean
  // DHCP range
  dhcp_range_start?: string
  dhcp_range_end?: string
  // Specs
  cpu_cores?: number
  memory_mb?: number
  storage_gb?: number
  // SSH
  ssh_authorized_keys?: string[]
  // Connection mode
  connection_mode?: ConnectionMode // Default: 'direct'
  relay_id?: string // Required if connection_mode is 'relay'
  mgmt_ip?: string // Required if connection_mode is 'relay'
}

export type CloudGatewayUpdate = {
  name?: string
  nat_enabled?: boolean
  firewall_enabled?: boolean
  vpn_enabled?: boolean
  dhcp_server_enabled?: boolean
  lan_ip?: string
  dhcp_range_start?: string
  dhcp_range_end?: string
  ssh_authorized_keys?: string[]
}

export type GatewayDHCPConfig = {
  dhcp_range_start: string | null
  dhcp_range_end: string | null
  gateway_ip: string | null
  subnet_cidr: string | null
  has_subnet: boolean
}

export type GatewayStatusResponse = {
  id: string
  name: string
  status: CloudGatewayStatus
  status_message?: string | null
  vm_status?: string | null
  uptime?: number | null
  cpu_usage?: number | null
  memory_usage?: number | null
  wan_connected?: boolean | null
  wan_ip_current?: string | null
  lan_ip_current?: string | null
  lan_connected?: boolean | null
  // Connection mode status
  connection_mode: ConnectionMode
  is_connected: boolean
  last_heartbeat_at?: string | null
  checked_at: string
}

export type GatewayActionResponse = {
  id: string
  action: string
  status: CloudGatewayStatus
  message: string
  task_id?: string | null
}

export type GatewayDeployResponse = {
  id: string
  name: string
  status: CloudGatewayStatus
  message: string
  task_id?: string | null
}

export type GatewayListResponse = {
  gateways: CloudGateway[]
  total: number
}

export type RDPGatewayProvisionStatus =
  | 'not_initialized'
  | 'provisioning'
  | 'active'
  | 'inactive'
  | 'error'

export type RDPGatewayCertificateStatus = 'no_cert' | 'valid' | 'expiring_soon' | 'unknown'

export type RDPGatewayActionResponse = {
  success: boolean
  rdpgw_fqdn?: string | null
  rdpgw_provision_status: RDPGatewayProvisionStatus | string
  task_id?: string | null
  message: string
}

export type RDPGatewayStatusResponse = {
  rdpgw_enabled: boolean
  rdpgw_fqdn?: string | null
  // Mantido por compatibilidade com o backend; agora representa o label derivado do UUID do gateway.
  rdpgw_org_slug?: string | null
  rdpgw_cert_issued: boolean
  rdpgw_cert_expires_at?: string | null
  rdpgw_provision_status: RDPGatewayProvisionStatus | string
  status_source: 'database' | 'database+agent' | string
  gateway_connected: boolean
  live_status_available: boolean
  service_installed?: boolean | null
  service_running?: boolean | null
  config_exists?: boolean | null
  cert_status?: RDPGatewayCertificateStatus | null
  status_message?: string | null
  recommended_action?: string | null
  last_error?: string | null
}

export type GatewayTemplate = {
  id: string
  name: string
  proxmox_vmid: number
  proxmox_node: string
  cpu_cores?: number | null
  memory_mb?: number | null
  disk_size_gb?: number | null
  description?: string | null
  tags?: string[] | null
}

export type GatewayTemplatesResponse = {
  templates: GatewayTemplate[]
  total: number
}

export type GatewayClusterInfoResponse = {
  cluster_id: string
  cluster_name: string
  templates: GatewayTemplate[]
  total: number
}

// ============================================
// Gateway Products (B2B2B)
// ============================================

export type GatewayProductSpecs = {
  cpu_cores: number
  memory_gb: number
  storage_gb: number
}

export type GatewayProduct = {
  product_id: string
  product_name: string
  product_description?: string | null
  template_id: string
  template_name: string
  cluster_id: string
  cluster_name: string
  specs: GatewayProductSpecs
  base_price: number
  currency: string
  billing_cycle: string
  first_vpc_free?: boolean
  /**
   * false = a rede WAN do cluster não entrega DHCP. O portal não deve oferecer
   * a opção, senão o cliente cria um gateway que nunca conecta.
   */
  wan_dhcp_available?: boolean
}

export type GatewayProductsResponse = {
  products: GatewayProduct[]
  total: number
}

// ============================================
// API Functions
// ============================================

/**
 * Get the gateway for the current organization
 */
export async function getMyGateway(): Promise<CloudGateway> {
  // apiClient.get already returns response.data, so we don't need to access .data again
  return apiClient.get<CloudGateway>(`${BASE_URL}/gateway`)
}

/**
 * Check if the organization has a gateway
 */
export async function hasGateway(): Promise<boolean> {
  try {
    await getMyGateway()
    return true
  } catch (error: any) {
    if (error.response?.status === 404) {
      return false
    }
    throw error
  }
}

/**
 * Lista todos os Cloud Gateways da organização do usuário (multi-VPC).
 * Cada GW inclui attached_vpc populada. Substitui o singular getGateway()
 * quando a org tem mais de 1 GW.
 */
export async function listMyGateways(): Promise<CloudGatewayListResponse> {
  return apiClient.get<CloudGatewayListResponse>(`${BASE_URL}/gateways`)
}

/**
 * Preview do preço aplicado se o usuário criar um GW deste produto agora.
 * Considera Product.first_vpc_free e o número atual de GWs ativos da org.
 * UI usa pra mostrar "Primeiro gateway: incluso" vs "Nº 2 — R$ X/mês".
 */
export async function getGatewayBillingPreview(productId: string): Promise<GatewayBillingPreview> {
  return apiClient.get<GatewayBillingPreview>(
    `${BASE_URL}/gateway/billing-preview?product_id=${encodeURIComponent(productId)}`,
  )
}

/**
 * Move o Cloud Gateway pra outra VPC da mesma organização.
 * Atualiza o net1 no Proxmox preservando MAC + rebind do attachment +
 * dispara ReconfigureNotify pro agent (1.4.47+ reconfigura eth1 sozinho).
 */
export async function changeGatewayNetwork(
  gatewayId: string,
  data: ChangeGatewayNetworkRequest,
): Promise<CloudGateway> {
  return apiClient.patch<CloudGateway>(`${BASE_URL}/gateway/${gatewayId}/network`, data)
}

/**
 * Troca o endereçamento da WAN do gateway (DHCP ↔ IP do pool ↔ estático).
 *
 * Reescreve o `ipconfig0` no Proxmox e REINICIA a VM para o cloud-init
 * reaplicar a rede — o tráfego do cliente cai por alguns segundos. Confirme
 * com o usuário antes de chamar.
 */
export async function changeGatewayWan(
  gatewayId: string,
  data: ChangeGatewayWanRequest,
): Promise<ChangeGatewayWanResponse> {
  return apiClient.patch<ChangeGatewayWanResponse>(`${BASE_URL}/gateway/${gatewayId}/wan`, data)
}

/**
 * IPs públicos que o portal pode oferecer para a WAN do gateway no cluster:
 * os que a org já comprou e estão livres + o estoque do pool público.
 */
export async function getGatewayWanAvailableIPs(clusterId: string): Promise<GatewayWanIPsResponse> {
  return apiClient.get<GatewayWanIPsResponse>(`${BASE_URL}/gateway/wan/available-ips`, {
    params: { cluster_id: clusterId },
  })
}

/**
 * Get available gateway products for the current organization.
 * Uses B2B2B hierarchy to find products:
 * 1. Products from customer's reseller
 * 2. Public products from Cloud Provider
 *
 * Each product includes cluster and template information.
 */
export async function getMyGatewayProducts(): Promise<GatewayProductsResponse> {
  return apiClient.get<GatewayProductsResponse>(`${BASE_URL}/gateway/templates`)
}

/**
 * @deprecated Use getMyGatewayProducts() instead
 */
export async function getMyGatewayTemplates(): Promise<GatewayProductsResponse> {
  return getMyGatewayProducts()
}

/**
 * Get available gateway templates for a specific cluster (admin/reseller use)
 * @deprecated Use getMyGatewayTemplates() for end customers
 */
export async function getGatewayTemplates(clusterId: string): Promise<GatewayTemplatesResponse> {
  return apiClient.get<GatewayTemplatesResponse>(`${BASE_URL}/gateway/templates/${clusterId}`)
}

/**
 * Create a new gateway
 */
export async function createGateway(data: CloudGatewayCreate): Promise<CloudGateway> {
  return apiClient.post<CloudGateway>(`${BASE_URL}/gateway`, data)
}

/**
 * Get DHCP configuration for the gateway's subnet
 */
export async function getGatewayDHCPConfig(): Promise<GatewayDHCPConfig> {
  return apiClient.get<GatewayDHCPConfig>(`${BASE_URL}/gateway/dhcp`)
}

/**
 * Update the gateway
 */
export async function updateGateway(data: CloudGatewayUpdate): Promise<CloudGateway> {
  return apiClient.put<CloudGateway>(`${BASE_URL}/gateway`, data)
}

/**
 * Delete the gateway
 */
export async function deleteGateway(): Promise<void> {
  await apiClient.delete(`${BASE_URL}/gateway`)
}

/**
 * Get real-time status of the gateway
 */
export async function getGatewayStatus(): Promise<GatewayStatusResponse> {
  return apiClient.get<GatewayStatusResponse>(`${BASE_URL}/gateway/status`)
}

export async function initRDPGateway(): Promise<RDPGatewayActionResponse> {
  return apiClient.post<RDPGatewayActionResponse>(`${BASE_URL}/gateway/rdpgw/init`, {})
}

export async function getRDPGatewayStatus(): Promise<RDPGatewayStatusResponse> {
  return apiClient.get<RDPGatewayStatusResponse>(`${BASE_URL}/gateway/rdpgw/status`)
}

export async function reconcileRDPGateway(): Promise<RDPGatewayActionResponse> {
  return apiClient.post<RDPGatewayActionResponse>(`${BASE_URL}/gateway/rdpgw/reconcile`, {})
}

export async function getRDPGatewayLogs(lines: number = 100): Promise<GatewayCommandResult> {
  return apiClient.get<GatewayCommandResult>(`${BASE_URL}/gateway/rdpgw/logs`, {
    params: { lines },
  })
}

export async function restartRDPGateway(): Promise<GatewayCommandResult> {
  return apiClient.post<GatewayCommandResult>(`${BASE_URL}/gateway/rdpgw/restart`, {})
}

/**
 * Deploy the gateway VM
 */
export async function deployGateway(): Promise<GatewayDeployResponse> {
  return apiClient.post<GatewayDeployResponse>(`${BASE_URL}/gateway/deploy`, {})
}

/**
 * Start the gateway VM
 */
export async function startGateway(): Promise<GatewayActionResponse> {
  return apiClient.post<GatewayActionResponse>(`${BASE_URL}/gateway/start`, {})
}

/**
 * Stop the gateway VM
 */
export async function stopGateway(): Promise<GatewayActionResponse> {
  return apiClient.post<GatewayActionResponse>(`${BASE_URL}/gateway/stop`, {})
}

/**
 * Reboot the gateway VM
 */
export async function rebootGateway(): Promise<GatewayActionResponse> {
  return apiClient.post<GatewayActionResponse>(`${BASE_URL}/gateway/reboot`, {})
}

/**
 * Reconfigure the gateway (re-apply nftables, NAT, DHCP)
 */
export async function reconfigureGateway(): Promise<GatewayCommandResult> {
  return apiClient.post<GatewayCommandResult>(`${BASE_URL}/gateway/reconfigure`, {})
}

/**
 * Rebuild the gateway VM (delete + redeploy, preserves all rules)
 */
export async function rebuildGateway(): Promise<GatewayActionResponse> {
  return apiClient.post<GatewayActionResponse>(`${BASE_URL}/gateway/rebuild`, {})
}

/**
 * Delete the gateway VM (keeps configuration)
 */
export async function deleteGatewayVM(): Promise<GatewayActionResponse> {
  return apiClient.delete<GatewayActionResponse>(`${BASE_URL}/gateway/vm`)
}

// ============================================
// Helper Functions
// ============================================

export function getStatusColor(status: CloudGatewayStatus): string {
  const colors: Record<CloudGatewayStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    provisioning: 'bg-blue-100 text-blue-800',
    configuring: 'bg-blue-100 text-blue-800',
    running: 'bg-green-100 text-green-800',
    stopped: 'bg-gray-100 text-gray-800',
    error: 'bg-red-100 text-red-800',
    deleting: 'bg-orange-100 text-orange-800',
    rebuilding: 'bg-amber-100 text-amber-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}

export function getStatusLabel(status: CloudGatewayStatus): string {
  const labels: Record<CloudGatewayStatus, string> = {
    pending: 'Pendente',
    provisioning: 'Provisionando',
    configuring: 'Configurando',
    running: 'Executando',
    stopped: 'Parado',
    error: 'Erro',
    deleting: 'Excluindo',
    rebuilding: 'Recriando...',
  }
  return labels[status] || status
}

export function getStatusIcon(status: CloudGatewayStatus): string {
  const icons: Record<CloudGatewayStatus, string> = {
    pending: 'Clock',
    provisioning: 'Loader2',
    configuring: 'Settings',
    running: 'CheckCircle',
    stopped: 'StopCircle',
    error: 'XCircle',
    deleting: 'Trash2',
    rebuilding: 'RefreshCw',
  }
  return icons[status] || 'HelpCircle'
}

export function formatUptime(seconds?: number | null): string {
  if (!seconds) return '-'

  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

export function formatPercentage(value?: number | null): string {
  if (value === null || value === undefined) return '-'
  return `${(value * 100).toFixed(1)}%`
}

export function formatMemoryMB(mb: number): string {
  if (mb >= 1024) {
    return `${(mb / 1024).toFixed(1)} GB`
  }
  return `${mb} MB`
}

export function getConnectionModeLabel(mode: ConnectionMode): string {
  const labels: Record<ConnectionMode, string> = {
    direct: 'gRPC Direto',
    relay: 'Via Relay',
  }
  return labels[mode] || mode
}

export function getConnectionModeColor(mode: ConnectionMode, isConnected: boolean): string {
  if (!isConnected) {
    return 'bg-gray-100 text-gray-600'
  }
  const colors: Record<ConnectionMode, string> = {
    direct: 'bg-green-100 text-green-800',
    relay: 'bg-blue-100 text-blue-800',
  }
  return colors[mode] || 'bg-gray-100 text-gray-800'
}

export function getConnectionStatusLabel(mode: ConnectionMode, isConnected: boolean): string {
  if (!isConnected) {
    return 'Desconectado'
  }
  return mode === 'direct' ? 'Conectado (gRPC)' : 'Conectado (Relay)'
}

export function formatLastHeartbeat(timestamp?: string | null): string {
  if (!timestamp) return 'Nunca'

  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)

  if (diffSecs < 60) return 'Agora mesmo'
  if (diffMins < 60) return `${diffMins} min atrás`
  if (diffHours < 24) return `${diffHours}h atrás`
  return date.toLocaleDateString('pt-BR')
}

export function getRDPGatewayProvisionStatusColor(status?: string | null): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800'
    case 'provisioning':
      return 'bg-amber-100 text-amber-800'
    case 'inactive':
      return 'bg-slate-100 text-slate-700'
    case 'error':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

export function getRDPGatewayProvisionStatusLabel(status?: string | null): string {
  switch (status) {
    case 'active':
      return 'Ativo'
    case 'provisioning':
      return 'Provisionando...'
    case 'inactive':
      return 'Inativo'
    case 'error':
      return 'Erro'
    case 'not_initialized':
      return 'Não Inicializado'
    default:
      return status || 'Desconhecido'
  }
}

export function getRDPGatewayCertificateStatusColor(status?: string | null): string {
  switch (status) {
    case 'valid':
      return 'bg-green-100 text-green-800'
    case 'expiring_soon':
      return 'bg-amber-100 text-amber-800'
    case 'no_cert':
      return 'bg-gray-100 text-gray-700'
    case 'unknown':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

export function getRDPGatewayCertificateStatusLabel(status?: string | null): string {
  switch (status) {
    case 'valid':
      return 'Válido'
    case 'expiring_soon':
      return 'Expira em Breve'
    case 'no_cert':
      return 'Sem Certificado'
    case 'unknown':
      return 'Indeterminado'
    default:
      return status || 'Sem dados'
  }
}

// ============================================
// Firewall, Port Forward & DHCP Types
// ============================================

export type GatewayFirewallRule = {
  id?: string
  direction: string
  action: string
  protocol?: string | null
  source?: string | null
  destination?: string | null
  port?: number | null
  port_range?: string | null
  comment?: string | null
  handle?: number
  enabled?: boolean
  /** Preenchido quando o gateway NÃO aplicou a regra: ela está no banco, não no nftables. */
  sync_error?: string | null
  synced_at?: string | null
  /**
   * Quem manda na regra (#955). `user` é o default; `system` é regra que uma FEATURE abriu
   * (port forward, RDP Gateway…) — ela aparece na lista, mas não se edita nem se remove: o
   * caminho para fechar a porta é desligar a feature.
   */
  managed_by?: 'user' | 'system'
  /** A feature dona: `port_forward:<uuid>`, `rdpgw`, `vpn:<uuid>`, `lb:<uuid>`. */
  owner_ref?: string | null
}

/** Rótulo curto da feature que abriu a regra — usado no selo da aba Firewall. */
export function getFirewallRuleOriginLabel(rule: GatewayFirewallRule): string | null {
  if (rule.managed_by !== 'system') return null
  const ref = rule.owner_ref || ''
  if (ref.startsWith('port_forward:')) return 'Port forward'
  if (ref === 'rdpgw') return 'RDP Gateway'
  return 'Gerenciada'
}

/** O que `POST/PUT /gateway/port-forwards` devolve (o recurso, não o envelope do agent). */
export type GatewayPortForward = {
  id: string
  protocol: string
  external_port: number
  internal_ip: string
  internal_port: number
  comment?: string | null
  enabled: boolean
  /** Preenchido quando o gateway NÃO aplicou a regra: ela está no banco, não no nftables. */
  sync_error?: string | null
  synced_at?: string | null
  created_at?: string
}

export type GatewayFirewallRuleCreate = {
  direction: string
  action: string
  protocol?: string
  source?: string
  destination?: string
  port?: number
  port_range?: string
  comment?: string
}

export type GatewayPortForwardCreate = {
  protocol: string
  external_port: number
  internal_ip: string
  internal_port: number
  comment?: string
}

export type GatewayFirewallRuleUpdate = {
  direction?: string
  action?: string
  protocol?: string
  source?: string | null
  destination?: string | null
  /** `null` LIMPA a porta. `undefined` some do JSON e o backend (`exclude_unset`)
   *  mantém o valor antigo — que é o que impedia trocar TCP por "Qualquer". */
  port?: number | null
  port_range?: string | null
  comment?: string | null
}

export type GatewayPortForwardUpdate = {
  protocol?: string
  external_port?: number
  internal_ip?: string
  internal_port?: number
  // `null` limpa a descrição; `undefined` a mantém. O backend usa `exclude_unset`, então
  // `undefined` some do JSON e o campo fica "não enviado" — e o agent MESCLA por chave
  // presente, mantendo a descrição velha no gateway. Mesma razão de
  // `GatewayFirewallRuleUpdate` (obrigatório NÃO aceita `null`: o schema devolve 422).
  comment?: string | null
}

export type GatewayDHCPLease = {
  ip: string
  mac: string
  hostname?: string | null
  expires?: string | null
}

export type GatewayCommandResult = {
  success: boolean
  result?: Record<string, any> | null
  error?: string | null
  via?: string
}

// ============================================
// Firewall API Functions
// ============================================

/**
 * Listagem de regras: o backend devolve `{rules, total}` — plano, direto do banco.
 * NÃO é o envelope `{success, result}` do agent (que os endpoints de DHCP/logs ainda usam).
 * Ler `result.rules` aqui devolvia `undefined` e a aba ficava permanentemente vazia (BUG-018).
 */
export type GatewayFirewallRulesResponse = {
  rules: GatewayFirewallRule[]
  total: number
  /** Estado ao vivo do nftables (só com `?live=true` e gateway conectado). */
  live?: Record<string, any> | null
  /** Por que o estado ao vivo não veio (ex.: "Gateway não conectado"). */
  live_error?: string
}

export type GatewayPortForwardsResponse = {
  port_forwards: GatewayPortForward[]
  total: number
}

export async function getFirewallRules(): Promise<GatewayFirewallRulesResponse> {
  return apiClient.get<GatewayFirewallRulesResponse>(`${BASE_URL}/gateway/firewall/rules`)
}

export async function addFirewallRule(
  data: GatewayFirewallRuleCreate,
): Promise<GatewayFirewallRule> {
  return apiClient.post<GatewayFirewallRule>(`${BASE_URL}/gateway/firewall/rules`, data)
}

export async function removeFirewallRule(ruleId: string): Promise<GatewayCommandResult> {
  return apiClient.delete<GatewayCommandResult>(`${BASE_URL}/gateway/firewall/rules/${ruleId}`)
}

export async function updateFirewallRule(
  ruleId: string,
  data: GatewayFirewallRuleUpdate,
): Promise<GatewayFirewallRule> {
  return apiClient.put<GatewayFirewallRule>(`${BASE_URL}/gateway/firewall/rules/${ruleId}`, data)
}

export async function saveFirewallRules(): Promise<GatewayCommandResult> {
  return apiClient.post<GatewayCommandResult>(`${BASE_URL}/gateway/firewall/save`, {})
}

// ============================================
// Port Forward API Functions
// ============================================

/** A aba de Port Forwards não consumia este endpoint — lia a lista de firewall e via vazio. */
export async function listPortForwards(): Promise<GatewayPortForwardsResponse> {
  return apiClient.get<GatewayPortForwardsResponse>(`${BASE_URL}/gateway/port-forwards`)
}

export async function addPortForward(data: GatewayPortForwardCreate): Promise<GatewayPortForward> {
  return apiClient.post<GatewayPortForward>(`${BASE_URL}/gateway/port-forwards`, data)
}

export async function removePortForward(ruleId: string): Promise<GatewayCommandResult> {
  return apiClient.delete<GatewayCommandResult>(`${BASE_URL}/gateway/port-forwards/${ruleId}`)
}

export async function updatePortForward(
  ruleId: string,
  data: GatewayPortForwardUpdate,
): Promise<GatewayPortForward> {
  return apiClient.put<GatewayPortForward>(`${BASE_URL}/gateway/port-forwards/${ruleId}`, data)
}

// ============================================
// DHCP Leases API Function
// ============================================

export async function getDHCPLeases(): Promise<GatewayCommandResult> {
  return apiClient.get<GatewayCommandResult>(`${BASE_URL}/gateway/dhcp/leases`)
}

// ============================================
// Logs API Function
// ============================================

export async function getGatewayLogs(lines: number = 100): Promise<GatewayCommandResult> {
  return apiClient.get<GatewayCommandResult>(`${BASE_URL}/gateway/logs`, { params: { lines } })
}

/**
 * Get serial console ticket for the gateway
 */
export async function getGatewayConsoleTicket(): Promise<ConsoleTicket> {
  return apiClient.post<ConsoleTicket>(`${BASE_URL}/gateway/console/ticket`)
}

// ============================================
// Export default object
// ============================================

export const gatewayApi = {
  getMyGateway,
  listMyGateways,
  hasGateway,
  getMyGatewayProducts,
  getMyGatewayTemplates, // deprecated, use getMyGatewayProducts
  getGatewayBillingPreview,
  createGateway,
  updateGateway,
  deleteGateway,
  changeGatewayNetwork,
  changeGatewayWan,
  getGatewayWanAvailableIPs,
  getGatewayStatus,
  initRDPGateway,
  getRDPGatewayStatus,
  reconcileRDPGateway,
  getRDPGatewayLogs,
  restartRDPGateway,
  getGatewayDHCPConfig,
  deployGateway,
  startGateway,
  stopGateway,
  rebootGateway,
  reconfigureGateway,
  rebuildGateway,
  deleteGatewayVM,
  // Firewall
  getFirewallRules,
  addFirewallRule,
  removeFirewallRule,
  updateFirewallRule,
  saveFirewallRules,
  // Port Forward
  listPortForwards,
  addPortForward,
  removePortForward,
  updatePortForward,
  // DHCP Leases
  getDHCPLeases,
  // Logs
  getGatewayLogs,
  // Console
  getGatewayConsoleTicket,
}

export default gatewayApi
